import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';
import DeleteAlert from '../../Common/Layout/DeleteAlert';
import Flatpickr from "react-flatpickr";
import Select from 'react-select';
import DefaultData from '../../CompanyData/DefaultData';
import airports from "../../Data/AirportList.json";

const initialFieldValues = {
    groupBookingRequestId: "00000000-0000-0000-0000-000000000000",
    companyId: "",
    agentCompanyId: "",
    journeyType: "One Way",
    adults: 1,
    kids: 0,
    infants: 0,
    preferredAirlines: "",
    comments: "",
    segments: [
        { from: "", to: "", departureDate: null } 
    ]
};

const GroupBookings = () => {
    const { user } = useAuth();
    const [listLoading, setListLoading] = useState(true);
    const [btnLoading, setBtnLoading] = useState(false);
    const [apiError, setApiError] = useState('');
    const [bookings, setBookings] = useState([]);
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({}); 
    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [addShow, setAddShow] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const airportOptions = useMemo(() =>
        airports.map(airport => ({
            value: airport.airportCode,
            label: `${airport.cityName} (${airport.airportCode}), ${airport.countryName}`
        })),
    []);

    const GetData = async (currentPage) => {
        if (!user?.cid) {
            setListLoading(false);
            return;
        }
        setListLoading(true);
        setApiError('');
        try {
            const res = await apiService.get(`${config.GETAGENTBOOKING}/${user.cid}`, pageSize, currentPage);
            setBookings(res.data || []);
            setTotalPages(res.totalPages || 0);
            setTotalRecords(res.totalRecords || 0);
            setPageNumber(res.pageNumber || currentPage);
            setPageSize(res.pageSize || 10);
        } catch (err) {
            setApiError("Failed to fetch booking requests. Please try again.");
            setBookings([]);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        if (user?.cid) {
            GetData(1);
        }
    }, [user, pageSize]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
    };

    const handleJourneyTypeChange = (e) => {
        const newJourneyType = e.target.value;
        const currentSegments = values.segments;
        let newSegments = [{ from: "", to: "", departureDate: null }];

        if (newJourneyType === 'Return') {
            const firstSeg = currentSegments[0] || { from: "", to: "" };
            newSegments = [
                { ...firstSeg, departureDate: firstSeg.departureDate || null },
                { from: firstSeg.to, to: firstSeg.from, departureDate: null }
            ];
        } else if (newJourneyType === 'MultiCity') {
            newSegments = [
                { from: "", to: "", departureDate: null },
                { from: "", to: "", departureDate: null }
            ];
        }
        setValues({ ...values, journeyType: newJourneyType, segments: newSegments });
        setErrors({}); 
    };

    const handleSegmentChange = (index, field, value) => {
        const newSegments = [...values.segments];
        newSegments[index][field] = value;

        if (values.journeyType === 'Return' && index === 0 && newSegments.length > 1) {
            if (field === 'from') newSegments[1].to = value;
            if (field === 'to') newSegments[1].from = value;
        }

        setValues({ ...values, segments: newSegments });
    };

    const addSegment = () => {
        setValues({
            ...values,
            segments: [...values.segments, { from: "", to: "", departureDate: null }]
        });
    };
    const removeSegment = (index) => {
        const newSegments = values.segments.filter((_, i) => i !== index);
        setValues({ ...values, segments: newSegments });
    };


const validate = () => {
    const newErrors = {};
    const segmentErrors = [];

    values.segments.forEach((segment, index) => {
        const currentSegmentErrors = {};
        if (!segment.from) currentSegmentErrors.from = true;
        if (!segment.to) currentSegmentErrors.to = true;
        if (!segment.departureDate) currentSegmentErrors.departureDate = true;
        
        if (Object.keys(currentSegmentErrors).length > 0) {
            segmentErrors[index] = currentSegmentErrors;
        }
    });

    if (segmentErrors.length > 0) newErrors.segments = segmentErrors;
    if (!values.adults || parseInt(values.adults, 10) < 1) newErrors.adults = true;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};

 
    const handleAddClose = () => {
        setAddShow(false);
        setValues(initialFieldValues);
        setApiError('');
        setErrors({}); 
        setBtnLoading(false);
    };

    const handleAddShow = () => {
        setValues(initialFieldValues);
        setErrors({});
        setApiError('');
        setAddShow(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');

        if (validate()) {
            setBtnLoading(true);
            
            const formData = {
                ...values,
                companyId: DefaultData.DEFAULTCOMPANYID,
                agentCompanyId: user.cid,
                adults: parseInt(values.adults, 10),
                kids: parseInt(values.kids, 10) || 0,
                infants: parseInt(values.infants, 10) || 0,
            };

            try {
                await apiService.create(config.CREATEBOOKINGREQUEST, formData);
                await GetData(pageNumber);
                handleAddClose();
            } catch (err) {
                setApiError("Failed to create the group booking request. Please try again.");
                console.error("API Error:", err);
            } finally {
                setBtnLoading(false);
            }
        }
    };

    // Handle deletion
    const handleDelete = async () => {
        if (itemToDelete !== null) {
            setApiError('');
            try {
                await apiService.remove(config.DELETEBOOKINGREQUEST, itemToDelete);
                const newPageNumber = bookings.length === 1 && pageNumber > 1 ? pageNumber - 1 : pageNumber;
                await GetData(newPageNumber);
            } catch (error) {
                setApiError("Failed to delete the request.");
            }
            handleCloseDeleteModal();
        }
    };

    // Modal/Delete controls
    const handleOpenDeleteModal = (id) => {
        setItemToDelete(id);
        setShowDeleteModal(true);
    };
    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setItemToDelete(null);
    };

    // Pagination controls
    const GetPageData = (number) => {
        if (pageNumber !== number) {
            GetData(number);
        }
    }
    const renderPageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
        <li className={`page-item ${pageNumber === number ? 'active' : ''}`} key={number}>
            <Link to="#" className="page-link" onClick={() => GetPageData(number)}>
                {number}
            </Link>
        </li>
    ));

    // Custom styles for react-select error state
    const selectErrorStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: '#dc3545',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#dc3545'
            }
        }),
    };

    return (
             <div className="card">
             <div className="card-body">
                <h1 className="card-title mb-4">Group Booking Requests</h1>
            <div className="me-2 pb-1">
                <div className="row g-4 align-items-center">
                    <div className="col-sm-auto ms-auto">
                        <div className="hstack gap-2">
                            <button type="button" className="btn btn-primary add-btn" onClick={handleAddShow} ><i className="ri-add-line align-bottom me-1" />  New Request</button>
                        </div>
                    </div>
                </div>
            </div>
           </div>
            {apiError && <div className="alert alert-danger alert-dismissible fade show"><button type="button" className="btn-close" onClick={() => setApiError('')}></button>{apiError}</div>}

            {listLoading ? (
                <div className="text-center p-5"><div className="spinner-border text-primary" /></div>
            ) : (
                <>
                    {bookings.length > 0 ? (
                        <>
                            {/* ... Table and Pagination JSX (no changes needed here) ... */}
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>S.No</th>
                                            <th>Itinerary</th>
                                            <th>Journey Type</th>
                                            <th>Travellers</th>
                                            <th>Status</th>
                                            <th>Requested On</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bookings.map((booking, index) => (
                                            <tr key={booking.groupBookingRequestId}>
                                                <td>{((pageNumber - 1) * pageSize) + index + 1}</td>
                                                <td><span className="fw-medium">{booking.primaryFrom}</span> to <span className="fw-medium">{booking.primaryTo}</span></td>
                                                <td>{booking.journeyType}</td>
                                                <td>{booking.totalTravellers}</td>
                                                <td><span className="badge bg-primary-subtle text-primary">{booking.status}</span></td>
                                                <td>{new Date(booking.createdDate).toLocaleDateString()}</td>
                                                <td className="text-end">
                                                    <div className="d-flex gap-2 justify-content-end">
                                                        <Link to="#" className="btn btn-sm btn-soft-danger" onClick={() => handleOpenDeleteModal(booking.groupBookingRequestId)} title="Delete">
                                                            <i className="ri-delete-bin-fill align-bottom" />
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                                <div className="flex-shrink-0">
                                    <div className="text-muted">
                                        Showing <span className="fw-semibold">{bookings.length}</span> of <span className="fw-semibold">{totalRecords}</span> Results
                                    </div>
                                </div>
                                <ul className="pagination pagination-separated pagination-sm mb-0">
                                    <li className={`page-item ${pageNumber === 1 ? 'disabled' : ''}`}>
                                        <Link to="#" className="page-link" onClick={() => GetPageData(pageNumber - 1)}>Previous</Link>
                                    </li>
                                    {renderPageNumbers}
                                    <li className={`page-item ${pageNumber >= totalPages ? 'disabled' : ''}`}>
                                        <Link to="#" className="page-link" onClick={() => GetPageData(pageNumber + 1)}>Next</Link>
                                    </li>
                                </ul>
                            </div>
                        </>
                    ) : (
                        <div className="text-center p-5 border rounded">
                            <i className="ri-inbox-2-line fs-1 text-muted mb-2"></i>
                            <p className="mb-0 text-muted">No group booking requests found.</p>
                        </div>
                    )}
                </>
            )}

            {/* Add/Edit Modal */}
            <Modal show={addShow} onHide={handleAddClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>New Group Booking Request</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit} noValidate>
                        {apiError && <div className="alert alert-danger">{apiError}</div>}
                        
                        {/* Journey Type */}
                        <div className="mb-3">
                            <div className="d-flex gap-4">
                                <div className="form-check"><input className="form-check-input" type="radio" name="journeyType" id="oneWay" value="One Way" checked={values.journeyType === 'One Way'} onChange={handleJourneyTypeChange} /><label className="form-check-label" htmlFor="oneWay">One Way</label></div>
                                <div className="form-check"><input className="form-check-input" type="radio" name="journeyType" id="return" value="Return" checked={values.journeyType === 'Return'} onChange={handleJourneyTypeChange} /><label className="form-check-label" htmlFor="return">Return</label></div>
                                <div className="form-check"><input className="form-check-input" type="radio" name="journeyType" id="multiCity" value="MultiCity" checked={values.journeyType === 'MultiCity'} onChange={handleJourneyTypeChange} /><label className="form-check-label" htmlFor="multiCity">Multi-City</label></div>
                            </div>
                        </div>

                        {/* Flight segments with validation */}
                        {values.segments.map((segment, index) => (
                            <div key={index} className="row align-items-start mb-3 g-2">
                                <div className="col-md-3">
                                    <Select placeholder="From" isClearable options={airportOptions} value={airportOptions.find(option => option.value === segment.from)} onChange={(selectedOption) => handleSegmentChange(index, 'from', selectedOption ? selectedOption.value : '')} isDisabled={values.journeyType === 'Return' && index === 1} styles={errors.segments?.[index]?.from ? selectErrorStyles : {}}/>
                                      {errors.segments?.[index]?.from && <div className="text-danger small mt-1">Origin is required.</div>}
                                </div>
                                <div className="col-md-3">
                                    <Select placeholder="To" isClearable options={airportOptions} value={airportOptions.find(option => option.value === segment.to)} onChange={(selectedOption) => handleSegmentChange(index, 'to', selectedOption ? selectedOption.value : '')} isDisabled={values.journeyType === 'Return' && index === 1} styles={errors.segments?.[index]?.to ? selectErrorStyles : {}} />
                                    {errors.segments?.[index]?.to && <div className="text-danger small mt-1">Destination is required.</div>}
                                </div>
                                <div className="col-md-4">
                                    <Flatpickr value={segment.departureDate} className={`form-control ${errors.segments?.[index]?.departureDate ? 'is-invalid' : ''}`} placeholder="Departure Date" options={{ altInput: true, altFormat: "d M Y", dateFormat: "Y-m-d", minDate: "today" }} onChange={([date]) => handleSegmentChange(index, 'departureDate', date)} />
                                    {errors.segments?.[index]?.departureDate && <div className="invalid-feedback d-block">Date is required.</div>}
                                </div>
                                {values.journeyType === 'MultiCity' && values.segments.length > 1 && (
                                    <div className="col-md-2 d-flex align-items-center">
                                        <button type="button" className="btn btn-sm btn-soft-danger" onClick={() => removeSegment(index)}>
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}

                        {values.journeyType === 'MultiCity' && ( <div className="text-end mb-3"><button type="button" className="btn btn-sm btn-info" onClick={addSegment}><i className="ri-add-line"></i> Add Another City</button></div>)}
                        <hr />
                        
                        {/* Passenger Details with validation */}
                        <div className="row g-3 mb-3">
                           <div className="col-md-4">
                                <label className="form-label">Adults (12+ yrs)</label>
                                <input type="number" name="adults" value={values.adults} onChange={handleInputChange} className={`form-control ${errors.adults ? 'is-invalid' : ''}`} min="1" />
                                {errors.adults && <div className="invalid-feedback">{errors.adults}</div>}
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Kids (2-12 yrs)</label>
                                <input type="number" name="kids" value={values.kids} onChange={handleInputChange} className="form-control" min="0" />
                            </div>
                            <div className="col-md-4">
                               <label className="form-label">Infants (&lt; 2 yrs)</label>
                                <input type="number" name="infants" value={values.infants} onChange={handleInputChange} className="form-control" min="0" />
                            </div>
                        </div>

                        {/* Other Details */}
                        <div className="mb-3">
                           <label className="form-label">Preferred Airlines (Optional)</label>
                            <input type="text" name="preferredAirlines" value={values.preferredAirlines} onChange={handleInputChange} className="form-control" placeholder="e.g., Indigo, Vistara" />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Remarks (Optional)</label>
                            <textarea name="comments" value={values.comments} onChange={handleInputChange} className="form-control" rows="3" placeholder="Any special requests or comments..."></textarea>
                        </div>
                        
                        <div className="hstack gap-2 justify-content-end mt-4">
                            <button type="button" className="btn btn-light" onClick={handleAddClose}>Cancel</button>
                            <button type="submit" className="btn btn-success" disabled={btnLoading}>
                                {btnLoading ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Submitting...</> : 'Submit Request'}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
            
            <DeleteAlert show={showDeleteModal} handleClose={handleCloseDeleteModal} handleDelete={handleDelete} />
        </div>
    );
};

export default GroupBookings;
